import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/lib/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";
import { isNativeApp } from "@/lib/platform";
import { toast } from "sonner";
import PaymentForm from "@/components/PaymentForm";
import OtpDialog from "@/components/OtpDialog";
import SafeAvatar from "@/components/SafeAvatar";
import DeleteAccountButton from "@/components/DeleteAccountButton";
import { SocialLinks, SOCIAL_PLATFORMS } from "@/components/SocialLinks";
import { pushSupported, subscribeToPush, unsubscribeFromPush, currentPermission } from "@/lib/push";
import {
  ShieldCheck, Phone, Mail, MapPin, Star, Crown, Coins, Edit3, LogOut,
  Award, Megaphone, Check, Sparkles, Globe, Instagram, ArrowLeft, Bell, BellOff, Upload, Leaf,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter, DrawerClose
} from "@/components/ui/drawer";

export default function ProProfile() {
  const { user, setUser, logout, refresh } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [editing, setEditing] = useState(false);
  const [desc, setDesc] = useState(user?.description || "");
  const [website, setWebsite] = useState(user?.website || "");
  const [socials, setSocials] = useState(user?.socials || {});
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [payMode, setPayMode] = useState(null); // null | "sub" | "pack-s"|"pack-m"|"pack-l"
  const [packToBuy, setPackToBuy] = useState(null);
  const [badgePayOpen, setBadgePayOpen] = useState(false);
  const [otpChannel, setOtpChannel] = useState(null); // "email" | "phone"

  useEffect(() => { api.get("/wallet/me").then((r) => setWallet(r.data)); }, []);
  useEffect(() => {
    setDesc(user?.description || "");
    setWebsite(user?.website || "");
    setSocials(user?.socials || {});
  }, [user]);

  const subscribe = async (card) => {
    const { data } = await api.post("/subscription/activate", card);
    setUser(data.user); toast.success("Abonnement Premium activé !"); setPaywallOpen(false);
  };
  const buyPack = async (pack, card) => {
    const { data } = await api.post("/wallet/buy-tokens", { pack, ...card });
    setUser(data.user);
    const w = await api.get("/wallet/me"); setWallet(w.data);
    toast.success("Jetons ajoutés à votre compte !");
    setPackToBuy(null); setPaywallOpen(false);
  };
  const promoteBadge = async (card) => {
    const { data } = await api.post("/subscription/promote-badge", card);
    setUser(data.user); toast.success("Badge promu activé !");
    setBadgePayOpen(false);
  };
  const verifyPhone = () => setOtpChannel("phone");
  const verifyEmail = () => setOtpChannel("email");
  const saveDesc = async () => {
    try {
      const cleanSocials = Object.fromEntries(
        Object.entries(socials).filter(([_, v]) => (v || "").trim())
      );
      await api.patch("/profile/me", { description: desc, website, socials: cleanSocials });
      await refresh(); setEditing(false); toast.success("Profil mis à jour");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const uploadLogo = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo trop lourd (2 Mo max)"); return; }
    const r = new FileReader();
    r.onload = async () => {
      try {
        await api.patch("/profile/me", { logo: r.result });
        await refresh(); toast.success("Logo mis à jour");
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    r.readAsDataURL(file);
  };
  const [pushOn, setPushOn] = useState(false);
  const [rgeLoading, setRgeLoading] = useState(false);
  const verifyRge = async () => {
    setRgeLoading(true);
    try {
      const { data } = await api.post("/profile/rge-verify");
      if (data.rge_verified) {
        toast.success(`RGE vérifié : ${data.qualifications.length} qualification(s) trouvée(s)`);
      } else {
        toast.message("Aucune certification RGE trouvée pour votre SIRET/SIREN sur la base ADEME");
      }
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRgeLoading(false); }
  };
  useEffect(() => {
    (async () => {
      if (!pushSupported()) return;
      const perm = await currentPermission();
      setPushOn(perm === "granted" && !!user?.push_enabled);
    })();
  }, [user]);
  const togglePush = async () => {
    try {
      if (pushOn) {
        await unsubscribeFromPush();
        setPushOn(false);
        toast.message("Notifications désactivées");
      } else {
        await subscribeToPush();
        setPushOn(true);
        toast.success("Notifications activées");
        await refresh();
      }
    } catch (e) { toast.error(e.message || "Impossible d'activer les notifications"); }
  };

  if (!user) return null;

  return (
    <AppShell role="pro" title="Mon profil" right={
      <button onClick={logout} className="text-slate2 tap-scale" data-testid="logout-btn"><LogOut className="w-5 h-5" /></button>
    }>
      {/* Cover + avatar */}
      <div className="relative -mx-5 -mt-4 mb-16">
        <img src={user.cover || "https://images.unsplash.com/photo-1554995207-c18c203602cb?w=900"} alt="" className="w-full h-32 object-cover" />
        <div className="absolute left-5 -bottom-12">
          <SafeAvatar src={user.avatar} role="pro" name={user.name} className="w-24 h-24 rounded-3xl object-cover border-4 border-sand shadow" />
          <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-forest border-2 border-white flex items-center justify-center" data-testid="online-badge">
            <Check className="w-3 h-3 text-white" />
          </span>
        </div>
        {user.badge_promoted && (
          <span className="absolute right-4 bottom-3 bg-brand text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full">Promu</span>
        )}
      </div>

      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="font-heading font-bold text-xl text-ink leading-tight">{user.name}</h2>
            {user.logo && <img src={user.logo} alt="logo" className="w-6 h-6 rounded-md object-cover border border-border" data-testid="profile-header-logo" />}
          </div>
          {user.company && <div className="text-sm text-slate2">{user.company}</div>}
          <div className="flex items-center gap-2 mt-1.5 text-xs">
            <span className="bg-pro/10 text-pro px-2 py-0.5 rounded-full font-semibold">{user.trade?.replace(/-/g, " ")}</span>
            <span className="flex items-center gap-1 text-slate2"><MapPin className="w-3 h-3" />{user.area ? `${user.area}, ` : ""}{user.city}</span>
          </div>
          <div className="flex items-center gap-1 mt-2 text-sm">
            <Star className="w-4 h-4 fill-brand text-brand" />
            <span className="font-semibold text-ink">{user.rating || 0}</span>
            <span className="text-slate2">({user.reviews_count} avis)</span>
            <span className="text-xs text-forest ml-2">• actif maintenant</span>
          </div>
        </div>
      </div>

      {/* Subscription / wallet */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {user.subscription_active ? (
          <div className="col-span-2 bg-pro text-white rounded-2xl p-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/60">Abonnement actif</div>
              <div className="font-heading font-bold flex items-center gap-1.5"><Crown className="w-4 h-4 text-brand" /> Premium 9,99€/mois</div>
            </div>
            <Button onClick={async () => { await api.post("/subscription/cancel"); await refresh(); toast.message("Abonnement annulé"); }} variant="outline" size="sm" className="rounded-full border-white/30 text-white hover:bg-white/10 hover:text-white bg-transparent" data-testid="cancel-sub-btn">Annuler</Button>
          </div>
        ) : isNativeApp() ? (
          <div className="col-span-2 bg-white border border-border rounded-2xl p-4" data-testid="premium-android-hint">
            <div className="text-[10px] uppercase tracking-widest text-slate2">Premium</div>
            <div className="font-heading font-bold flex items-center gap-1.5 text-ink">
              <Crown className="w-4 h-4 text-brand" /> Bientôt disponible via Google Play
            </div>
            <div className="text-xs text-slate2 mt-1">
              L&apos;abonnement Premium arrive prochainement dans l&apos;application Android.
            </div>
          </div>
        ) : (
          <button
            onClick={async () => {
              try {
                track("pro_plan_premium_chosen", { source: "profile" });
                const { data } = await api.post("/subscription/mollie/checkout", { origin_url: window.location.origin });
                track("mollie_checkout_started", { mode: data.mode, payment_id: data.payment_id, source: "profile" });
                window.location.href = data.url;
              } catch (e) {
                toast.error(e.response?.data?.detail || "Impossible de démarrer le paiement");
              }
            }}
            className="col-span-2 bg-pro text-white rounded-2xl p-4 text-left tap-scale"
            data-testid="open-paywall-btn"
          >
            <div className="text-[10px] uppercase tracking-widest text-white/60">Premium</div>
            <div className="font-heading font-bold flex items-center gap-1.5"><Crown className="w-4 h-4 text-brand" /> Passer Premium — 9,99 €/mois</div>
            <div className="text-xs text-white/70 mt-1">Demandes illimitées + IA Pro + Priorité</div>
          </button>
        )}
        <div className="bg-white rounded-2xl border border-border p-3">
          <div className="flex items-center gap-1.5 text-xs text-slate2"><Coins className="w-3.5 h-3.5" /> Jetons</div>
          <div className="font-heading font-bold text-2xl text-ink mt-1">{user.tokens}</div>
        </div>
        <button onClick={() => setPaywallOpen(true)} className="bg-brand-light text-brand rounded-2xl border border-brand/30 p-3 text-left tap-scale" data-testid="buy-tokens-btn">
          <div className="text-xs font-semibold">Acheter</div>
          <div className="font-heading font-bold">Packs jetons</div>
        </button>
      </div>

      {/* Verification badges */}
      <div className="bg-white rounded-2xl border border-border p-4 mb-4">
        <h3 className="text-xs uppercase tracking-wider text-slate2 mb-3">Vérifications</h3>
        <div className="space-y-2.5">
          <Row icon={Phone} label="Téléphone" value={user.phone} verified={user.verified_phone} onVerify={verifyPhone} testid="verify-phone" />
          <Row icon={Mail} label="Email" value={user.email} verified={user.verified_email} onVerify={verifyEmail} testid="verify-email" />
          <Row
            icon={ShieldCheck}
            label={(user.identifier_type || (user.siren ? "siren" : "siret")).toUpperCase()}
            value={user.identifier || user.siret || user.siren}
            verified={!!(user.identifier || user.siret || user.siren)}
            testid="verify-identifier"
          />
        </div>
      </div>

      {/* RGE ADEME */}
      <div className={`rounded-2xl p-4 mb-4 border ${user.rge_verified ? "bg-forest/5 border-forest/30" : "bg-white border-border"}`} data-testid="rge-block">
        <div className="flex items-start gap-3">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${user.rge_verified ? "bg-forest text-white" : "bg-forest/10 text-forest"}`}>
            <Leaf className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-heading font-bold text-ink text-sm">Certification RGE (ADEME)</h3>
              {user.rge_verified && (
                <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-forest text-white flex items-center gap-1" data-testid="rge-verified-badge">
                  <Check className="w-3 h-3" /> Vérifié
                </span>
              )}
            </div>
            <p className="text-xs text-slate2 mt-1">
              Vérification gratuite via la base publique ADEME (Reconnu Garant de l&apos;Environnement).
            </p>
            {user.rge_verified && Array.isArray(user.rge_qualifications) && user.rge_qualifications.length > 0 && (
              <ul className="mt-2 space-y-1" data-testid="rge-qualifications-list">
                {user.rge_qualifications.slice(0, 4).map((q, i) => (
                  <li key={i} className="text-[11px] text-forest bg-forest/10 rounded-lg px-2 py-1">
                    <span className="font-semibold">{q.qualification}</span>
                    {q.domaine ? <span className="text-slate2"> · {q.domaine}</span> : null}
                  </li>
                ))}
                {user.rge_qualifications.length > 4 && (
                  <li className="text-[11px] text-slate2 italic">+ {user.rge_qualifications.length - 4} autre(s)…</li>
                )}
              </ul>
            )}
            <Button
              onClick={verifyRge}
              disabled={rgeLoading || !(user.identifier || user.siret || user.siren)}
              size="sm"
              className="mt-3 rounded-full bg-forest hover:bg-forest/90 text-white"
              data-testid="verify-rge-btn"
            >
              {rgeLoading ? "Vérification…" : user.rge_verified ? "Revérifier" : "Vérifier mon RGE"}
            </Button>
          </div>
        </div>
      </div>

      {/* Logo entreprise */}
      <div className="bg-white rounded-2xl border border-border p-4 mb-4">
        <h3 className="text-xs uppercase tracking-wider text-slate2 mb-3 flex items-center gap-1.5"><Upload className="w-3.5 h-3.5" /> Logo entreprise</h3>
        <label className="flex items-center gap-3 cursor-pointer tap-scale">
          <input type="file" accept="image/*" className="hidden" data-testid="profile-logo-input" onChange={(e) => uploadLogo(e.target.files?.[0])} />
          {user.logo ? (
            <>
              <img src={user.logo} alt="logo" className="w-16 h-16 rounded-xl object-cover border border-border" />
              <div className="flex-1">
                <div className="text-sm font-medium text-ink">Logo actif</div>
                <div className="text-xs text-slate2">Cliquez pour remplacer</div>
              </div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-xl border-2 border-dashed border-border flex items-center justify-center">
                <Upload className="w-5 h-5 text-slate-400" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium text-ink">Ajouter un logo</div>
                <div className="text-xs text-slate2">2 Mo max · optionnel</div>
              </div>
            </>
          )}
        </label>
      </div>

      {/* Notifications push */}
      {pushSupported() && (
        <button
          onClick={togglePush}
          data-testid="toggle-push-btn"
          className={`w-full rounded-2xl p-4 mb-4 text-left tap-scale border ${pushOn ? "bg-forest/5 border-forest/30" : "bg-white border-border"}`}
        >
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${pushOn ? "bg-forest text-white" : "bg-brand-light text-brand"}`}>
              {pushOn ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
            </div>
            <div className="flex-1">
              <div className="font-heading font-bold text-ink">
                {pushOn ? "Notifications actives" : "Activer les notifications"}
              </div>
              <div className="text-xs text-slate2">
                {pushOn ? "Vous serez alerté de chaque nouvelle demande dans votre zone" : "Recevez les nouvelles demandes de votre métier dans votre ville, en temps réel"}
              </div>
            </div>
            {pushOn && <span className="text-forest text-xs font-semibold">ON</span>}
          </div>
        </button>
      )}

      {/* Description + website */}
      <div className="bg-white rounded-2xl border border-border p-4 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs uppercase tracking-wider text-slate2">À propos</h3>
          <button onClick={() => setEditing((e) => !e)} className="text-brand text-xs font-semibold flex items-center gap-1 tap-scale" data-testid="edit-profile-btn">
            <Edit3 className="w-3.5 h-3.5" /> Modifier
          </button>
        </div>
        {editing ? (
          <div className="space-y-3">
            <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={4} placeholder="Décrivez votre activité, votre expérience…" className="rounded-xl" data-testid="desc-textarea" />
            <input value={website} onChange={(e) => setWebsite(e.target.value)} className="w-full border rounded-xl px-3 py-2 text-sm" placeholder="https://votre-site.fr" data-testid="website-input" />
            <div className="space-y-2 pt-1">
              <div className="text-[10px] uppercase tracking-widest text-slate2 font-semibold">Réseaux sociaux</div>
              {SOCIAL_PLATFORMS.map((p) => {
                const Ico = p.icon;
                return (
                  <div key={p.key} className="flex items-center gap-2">
                    <span className={`h-9 w-9 rounded-xl bg-bone border border-border flex items-center justify-center ${p.color} shrink-0`}>
                      <Ico className="w-4 h-4" />
                    </span>
                    <input
                      type="url"
                      value={socials[p.key] || ""}
                      onChange={(e) => setSocials((s) => ({ ...s, [p.key]: e.target.value }))}
                      placeholder={p.placeholder}
                      className="flex-1 min-w-0 border rounded-xl px-3 py-2 text-sm"
                      data-testid={`social-input-${p.key}`}
                    />
                  </div>
                );
              })}
            </div>
            <Button onClick={saveDesc} className="rounded-full bg-pro hover:bg-pro-hover w-full" data-testid="save-profile-btn">Enregistrer</Button>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate2 leading-relaxed">{user.description || "Ajoutez une présentation de votre activité."}</p>
            {user.website && (
              <a href={user.website} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-brand text-sm font-semibold mt-2"><Globe className="w-4 h-4" /> {user.website}</a>
            )}
            {user.socials && Object.values(user.socials).some((v) => (v || "").trim()) && (
              <div className="mt-3">
                <SocialLinks socials={user.socials} />
              </div>
            )}
          </>
        )}
      </div>

      {/* Certifications */}
      {user.certifications?.length > 0 && (
        <div className="bg-white rounded-2xl border border-border p-4 mb-4">
          <h3 className="text-xs uppercase tracking-wider text-slate2 mb-3 flex items-center gap-1.5"><Award className="w-3.5 h-3.5" /> Certifications & garanties</h3>
          <div className="flex flex-wrap gap-2">
            {user.certifications.map((c) => (
              <span key={c} className="bg-forest/10 text-forest text-xs font-semibold px-2.5 py-1 rounded-full">{c}</span>
            ))}
          </div>
        </div>
      )}

      {/* Badge promo */}
      <button onClick={() => setBadgePayOpen(true)} disabled={user.badge_promoted} className="w-full bg-gradient-to-br from-brand to-brand-hover text-white rounded-2xl p-4 mb-4 text-left tap-scale disabled:opacity-60" data-testid="promote-badge-btn">
        <div className="flex items-center gap-3">
          <Megaphone className="w-6 h-6" />
          <div className="flex-1">
            <div className="font-heading font-bold">{user.badge_promoted ? "Badge Promu actif" : "Mettre en avant mon activité"}</div>
            <div className="text-xs text-white/80">9,99€/mois · Apparaître en haut des résultats</div>
          </div>
        </div>
      </button>

      {/* Paywall drawer */}
      <Drawer open={paywallOpen} onOpenChange={(v) => { setPaywallOpen(v); if (!v) { setPayMode(null); setPackToBuy(null); } }}>
        <DrawerContent>
          {!payMode && (
            <>
              <DrawerHeader className="text-left">
                <DrawerTitle className="font-heading text-2xl flex items-center gap-2"><Crown className="w-6 h-6 text-brand" /> Plan Pro</DrawerTitle>
                <DrawerDescription>Choisissez l'option qui vous correspond le mieux.</DrawerDescription>
              </DrawerHeader>
              <div className="px-4 space-y-3">
                <button onClick={() => setPayMode("sub")} className="w-full bg-pro text-white rounded-2xl p-4 text-left tap-scale" data-testid="choose-sub-btn">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-white/60">Recommandé</div>
                      <div className="font-heading font-bold text-lg">Premium · 9,99€/mois</div>
                      <ul className="text-xs text-white/80 mt-2 space-y-0.5">
                        <li>• Leads illimités</li>
                        <li>• IA Pro</li>
                        <li>• Priorité sur les leads</li>
                      </ul>
                    </div>
                    <Crown className="w-7 h-7 text-brand" />
                  </div>
                </button>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate2 mb-2">Packs jetons</div>
                  <div className="grid grid-cols-3 gap-2">
                    {wallet && Object.entries(wallet.packs).map(([k, p]) => (
                      <button key={k} onClick={() => { setPackToBuy(k); setPayMode(`pack-${k}`); }} className="bg-white border border-border rounded-2xl p-3 tap-scale" data-testid={`pack-${k}-btn`}>
                        <div className="text-xs text-slate2 uppercase tracking-wider">Pack {k.toUpperCase()}</div>
                        <div className="font-heading font-bold text-xl text-ink">{p.tokens}j</div>
                        <div className="text-xs text-brand font-semibold">{p.price}€</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <DrawerFooter><DrawerClose asChild><Button variant="outline" className="rounded-full" data-testid="close-paywall-btn">Plus tard</Button></DrawerClose></DrawerFooter>
            </>
          )}
          {payMode === "sub" && (
            <div className="px-4 pb-4">
              <DrawerHeader className="text-left px-0">
                <button onClick={() => setPayMode(null)} className="flex items-center gap-1 text-sm text-slate2 mb-2"><ArrowLeft className="w-4 h-4" /> Retour</button>
                <DrawerTitle className="font-heading text-2xl">Abonnement Premium</DrawerTitle>
                <DrawerDescription>Facturé 9,99€/mois. Résiliable à tout moment.</DrawerDescription>
              </DrawerHeader>
              <PaymentForm amount="9,99 €" title="Premium mensuel" subtitle="Renouvellement automatique · Résiliable" ctaLabel="Payer" onPay={subscribe} />
            </div>
          )}
          {payMode && payMode.startsWith("pack-") && wallet && (
            <div className="px-4 pb-4">
              <DrawerHeader className="text-left px-0">
                <button onClick={() => { setPayMode(null); setPackToBuy(null); }} className="flex items-center gap-1 text-sm text-slate2 mb-2"><ArrowLeft className="w-4 h-4" /> Retour</button>
                <DrawerTitle className="font-heading text-2xl">Pack {packToBuy?.toUpperCase()}</DrawerTitle>
                <DrawerDescription>{wallet.packs[packToBuy].tokens} jetons pour débloquer des demandes.</DrawerDescription>
              </DrawerHeader>
              <PaymentForm amount={`${wallet.packs[packToBuy].price} €`} title={`${wallet.packs[packToBuy].tokens} jetons`} subtitle="Paiement unique" ctaLabel="Payer" onPay={(card) => buyPack(packToBuy, card)} />
            </div>
          )}
        </DrawerContent>
      </Drawer>

      {/* Badge promo drawer */}
      <Drawer open={badgePayOpen} onOpenChange={setBadgePayOpen}>        <DrawerContent>
          <div className="px-4 pb-4">
            <DrawerHeader className="text-left px-0">
              <DrawerTitle className="font-heading text-2xl">Badge Promu</DrawerTitle>
              <DrawerDescription>Mise en avant de votre fiche dans les résultats pro. 9,99€/mois.</DrawerDescription>
            </DrawerHeader>
            <PaymentForm amount="9,99 €" title="Badge Promu" subtitle="Mise en avant mensuelle" ctaLabel="Payer" onPay={promoteBadge} />
          </div>
        </DrawerContent>
      </Drawer>

      <OtpDialog
        open={!!otpChannel}
        onOpenChange={(v) => { if (!v) setOtpChannel(null); }}
        channel={otpChannel || "email"}
        onVerified={refresh}
      />

      <DeleteAccountButton testId="pro-delete-account-btn" />
    </AppShell>
  );
}

function Row({ icon: Icon, label, value, verified, onVerify, testid }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-slate2 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-slate2">{label}</div>
        <div className="text-sm text-ink truncate">{value || "—"}</div>
      </div>
      {verified ? (
        <span className="text-forest text-xs font-semibold flex items-center gap-1"><Check className="w-3.5 h-3.5" /> vérifié</span>
      ) : onVerify ? (
        <button onClick={onVerify} className="text-brand text-xs font-semibold tap-scale" data-testid={testid}>Vérifier</button>
      ) : (
        <span className="text-slate2 text-xs">non vérifié</span>
      )}
    </div>
  );
}
